import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import { ArrowLeft, Search, MapPin, Navigation, FileText, User, Clock, CreditCard, Mail, Phone, X, Loader2, CheckCircle, Mic, MicOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { addressApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import RgpdCheckbox from '../components/RgpdCheckbox';
import 'leaflet/dist/leaflet.css';
import { usePartner } from '../lib/partnerContext';
import L from 'leaflet';

// Fix Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const API = process.env.REACT_APP_BACKEND_URL;

// Component to handle map location updates
function LocationMarker({ position, setPosition }) {
  const map = useMap();
  
  useEffect(() => {
    if (position) {
      map.flyTo(position, 17);
    }
  }, [position, map]);

  useMapEvents({
    click(e) {
      setPosition([e.latlng.lat, e.latlng.lng]);
    },
  });

  return position ? (
    <Marker position={position}>
      <Popup>Position sélectionnée</Popup>
    </Marker>
  ) : null;
}

export default function RadarFoncierPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Partner tracking (URL ?partner= puis mémoire locale) — le client reste dans la marque blanche
  const partner = usePartner(searchParams.toString());
  const partnerSubdomain = partner.subdomain;
  
  // Map state
  const [position, setPosition] = useState(null);
  const [mapCenter, setMapCenter] = useState([43.2965, 5.3698]); // Marseille default
  const [loading, setLoading] = useState(false);
  
  // Search state
  const [searchMode, setSearchMode] = useState('address'); // 'gps', 'address', 'cadastre'
  const [addressSearch, setAddressSearch] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const addressDebounceRef = useRef(null);
  const [isListeningRadar, setIsListeningRadar] = useState(false);
  const [addressValidatedRadar, setAddressValidatedRadar] = useState(false);
  const radarRecognitionRef = useRef(null);
  const [cadastreSearch, setCadastreSearch] = useState({ commune: '', section: '', numero: '' });
  
  // Parcelle state
  const [selectedParcelle, setSelectedParcelle] = useState(null);
  const [parcelleLoading, setParcelleLoading] = useState(false);
  
  // Registration modal
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registerForm, setRegisterForm] = useState({ email: '', telephone: '' });
  const [registering, setRegistering] = useState(false);
  const [rgpdRadar, setRgpdRadar] = useState(false);
  const [clientId, setClientId] = useState(null);
  const [parcelleId, setParcelleId] = useState(null);
  
  // Payment modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  
  // Anti double-envoi plan
  const [planSent, setPlanSent] = useState(false);

  // Suivi par code
  const [showSuiviModal, setShowSuiviModal] = useState(false);
  const [suiviForm, setSuiviForm] = useState({ email: '', code: '' });
  const [suiviLoading, setSuiviLoading] = useState(false);

  const handleSuiviByCode = async () => {
    if (!suiviForm.email || !suiviForm.code) return;
    setSuiviLoading(true);
    try {
      const resp = await axios.get(`${API}/api/radar/suivi-by-code`, {
        params: { email: suiviForm.email, code: suiviForm.code.toUpperCase().trim() }
      });
      navigate(partner.link(`/radar/suivi?demande=${resp.data.demande_id}&email=${resp.data.email}`));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Code ou email invalide');
    } finally {
      setSuiviLoading(false);
    }
  };

  // Get user's location
  const handleGetLocation = () => {
    if (navigator.geolocation) {
      setLoading(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = [pos.coords.latitude, pos.coords.longitude];
          setPosition(coords);
          setMapCenter(coords);
          fetchParcelleAtPosition(coords[0], coords[1]);
          setLoading(false);
        },
        (err) => {
          toast.error('Impossible d\'obtenir votre position');
          setLoading(false);
        },
        { enableHighAccuracy: true }
      );
    } else {
      toast.error('Géolocalisation non supportée');
    }
  };

  // Mic handler for radar address
  const handleMicRadar = () => {
    if (isListeningRadar) {
      radarRecognitionRef.current?.stop();
      setIsListeningRadar(false);
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.lang = 'fr-FR';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    radarRecognitionRef.current = recognition;
    setIsListeningRadar(true);
    recognition.start();
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setAddressSearch(transcript);
      setAddressValidatedRadar(false);
      setIsListeningRadar(false);
      // Délai 300ms > onBlur 200ms
      if (transcript.length >= 3) {
        setTimeout(async () => {
          try {
            const res = await addressApi.autocomplete(transcript);
            const results = res.data || [];
            if (results.length > 0) {
              setAddressSuggestions(results);
              setShowAddressSuggestions(true);
            }
          } catch {}
        }, 300);
      }
    };
    recognition.onerror = () => setIsListeningRadar(false);
    recognition.onend = () => setIsListeningRadar(false);
  };

  // Search address
  const handleAddressSearch = async () => {
    if (!addressSearch.trim()) return;
    
    setLoading(true);
    try {
      const response = await axios.get(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(addressSearch)}&limit=1`
      );
      
      if (response.data.features && response.data.features.length > 0) {
        const feature = response.data.features[0];
        const coords = [feature.geometry.coordinates[1], feature.geometry.coordinates[0]];
        setPosition(coords);
        setMapCenter(coords);
        fetchParcelleAtPosition(coords[0], coords[1]);
      } else {
        toast.error('Adresse non trouvée');
      }
    } catch (error) {
      toast.error('Erreur lors de la recherche');
    } finally {
      setLoading(false);
    }
  };

  // Search by cadastre reference
  const handleCadastreSearch = async () => {
    if (!cadastreSearch.commune || !cadastreSearch.section || !cadastreSearch.numero) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    setLoading(true);
    try {
      // First get commune code
      const communeResponse = await axios.get(
        `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(cadastreSearch.commune)}&limit=1`
      );
      
      if (!communeResponse.data || communeResponse.data.length === 0) {
        toast.error('Commune non trouvée');
        return;
      }
      
      const commune = communeResponse.data[0];
      
      // Get parcelle info from cadastre API
      const cadastreResponse = await axios.get(
        `https://apicarto.ign.fr/api/cadastre/parcelle?code_insee=${commune.code}&section=${cadastreSearch.section.toUpperCase()}&numero=${cadastreSearch.numero.padStart(4, '0')}`
      );
      
      if (cadastreResponse.data.features && cadastreResponse.data.features.length > 0) {
        const parcelle = cadastreResponse.data.features[0];
        const centroid = getCentroid(parcelle.geometry.coordinates[0][0]);
        setPosition(centroid);
        setMapCenter(centroid);
        
        setSelectedParcelle({
          commune: commune.nom,
          code_insee: commune.code,
          code_postal: commune.codesPostaux?.[0] || '',
          section: parcelle.properties.section,
          numero: parcelle.properties.numero,
          surface: parcelle.properties.contenance,
          lat: centroid[0],
          lon: centroid[1]
        });
      } else {
        toast.error('Parcelle non trouvée');
      }
    } catch (error) {
      toast.error('Erreur lors de la recherche cadastrale');
    } finally {
      setLoading(false);
    }
  };

  // Fetch parcelle at position
  const fetchParcelleAtPosition = async (lat, lon) => {
    setParcelleLoading(true);
    try {
      // Get parcelle from cadastre API
      const response = await axios.get(
        `https://apicarto.ign.fr/api/cadastre/parcelle?geom={"type":"Point","coordinates":[${lon},${lat}]}`
      );
      
      if (response.data.features && response.data.features.length > 0) {
        const parcelle = response.data.features[0];
        const props = parcelle.properties;
        
        // Get commune info
        const communeResponse = await axios.get(
          `https://geo.api.gouv.fr/communes/${props.code_insee}`
        );
        const commune = communeResponse.data;
        
        setSelectedParcelle({
          commune: commune?.nom || props.nom_commune || 'Inconnue',
          code_insee: props.code_insee,
          code_postal: commune?.codesPostaux?.[0] || '',
          section: props.section,
          numero: props.numero,
          surface: props.contenance,
          lat: lat,
          lon: lon
        });
      } else {
        setSelectedParcelle(null);
        toast.info('Aucune parcelle trouvée à cette position');
      }
    } catch (error) {
      console.error('Error fetching parcelle:', error);
      setSelectedParcelle(null);
    } finally {
      setParcelleLoading(false);
    }
  };

  // Helper to get centroid of polygon
  const getCentroid = (coords) => {
    let latSum = 0, lonSum = 0;
    coords.forEach(coord => {
      latSum += coord[1];
      lonSum += coord[0];
    });
    return [latSum / coords.length, lonSum / coords.length];
  };

  // Handle map click
  useEffect(() => {
    if (position) {
      fetchParcelleAtPosition(position[0], position[1]);
    }
  }, [position]);

  // Handle registration
  const handleRegister = async () => {
    if (!registerForm.email.trim()) {
      toast.error('Veuillez entrer votre email');
      return;
    }
    
    setRegistering(true);
    try {
      const response = await axios.post(`${API}/api/radar/client/register`, {
        email: registerForm.email,
        telephone: registerForm.telephone,
        ...selectedParcelle,
        ...(searchMode === 'address' && addressSearch ? { adresse_saisie: addressSearch } : {}),
        ...(partnerSubdomain && { partner_subdomain: partnerSubdomain })
      });
      
      const newClientId = response.data.client_id;
      const newParcelleId = response.data.parcelle_id;
      setClientId(newClientId);
      setParcelleId(newParcelleId);
      setShowRegisterModal(false);

      // Envoi automatique du plan par email dans la foulée (évite le double-clic)
      try {
        if (!planSent) {
          setPlanSent(true);
          await axios.post(`${API}/api/radar/send-plan?client_id=${newClientId}&parcelle_id=${newParcelleId}${partnerSubdomain ? '&partner_subdomain=' + partnerSubdomain : ''}`);
          toast.success('Inscription réussie, plan envoyé par email !');
        }
      } catch (sendErr) {
        toast.success('Inscription réussie ! (Cliquez sur "Recevoir le plan" pour l\'envoi)');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setRegistering(false);
    }
  };

  // Send free plan
  const handleSendPlan = async () => {
    if (!clientId || !parcelleId) {
      setShowRegisterModal(true);
      return;
    }
    if (planSent) return; // Anti double-envoi
    try {
      setPlanSent(true);
      await axios.post(`${API}/api/radar/send-plan?client_id=${clientId}&parcelle_id=${parcelleId}${partnerSubdomain ? '&partner_subdomain=' + partnerSubdomain : ''}`);
      toast.success('Plan envoyé par email !');
    } catch (error) {
      setPlanSent(false);
      toast.error('Erreur lors de l\'envoi');
    }
  };

  // Handle payment
  const handlePayment = async () => {
    if (!clientId || !parcelleId) {
      setShowRegisterModal(true);
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/api/radar/create-checkout-session?client_id=${clientId}&parcelle_id=${parcelleId}${partnerSubdomain ? '&partner_subdomain=' + partnerSubdomain : ''}`
      );
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du paiement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="bg-black/30 backdrop-blur-sm border-b border-white/10">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 sm:gap-4 min-w-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(partner.home)}
              className="text-white/70 hover:text-white px-2 sm:px-3 shrink-0"
              data-testid="radar-back-btn"
            >
              <ArrowLeft className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Retour</span>
            </Button>
            {partner.isPartner && partner.logo && (
              <img src={partner.logo} alt={partner.name || 'Partenaire'} className="h-8 w-8 rounded-full object-cover border border-white/30 shrink-0" data-testid="radar-partner-logo" />
            )}
            <h1 className="text-base sm:text-xl font-bold text-white flex items-center gap-1.5 sm:gap-2 whitespace-nowrap min-w-0">
              <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-purple-400 shrink-0" />
              <span className="truncate">Radar Foncier</span>
              {partner.isPartner && partner.name && (
                <span className="text-sm font-normal text-white/60 hidden md:inline truncate" data-testid="radar-partner-name">• {partner.name}</span>
              )}
            </h1>
          </div>
          {!partnerSubdomain && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSuiviModal(true)}
              className="border-purple-400 text-purple-300 hover:bg-purple-500/20 hover:text-white shrink-0 px-2.5 sm:px-3 text-xs sm:text-sm"
              data-testid="btn-suivre-demande"
            >
              <Clock className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Suivre ma demande</span>
              <span className="sm:hidden ml-1.5">Suivi</span>
            </Button>
          )}
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Panel - Search (appears first on mobile) */}
          <div className="space-y-4 order-1 lg:order-1">
            {/* Search Mode Tabs */}
            <Card className="bg-white/10 border-white/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg">Rechercher une parcelle</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 mb-4">
                  <Button
                    variant={searchMode === 'gps' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSearchMode('gps')}
                    className={`flex-1 min-w-[80px] ${searchMode === 'gps' ? 'bg-purple-600' : 'bg-white/10 text-white border-white/20'}`}
                  >
                    <Navigation className="w-4 h-4 mr-1" /> GPS
                  </Button>
                  <Button
                    variant={searchMode === 'address' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSearchMode('address')}
                    className={`flex-1 min-w-[80px] ${searchMode === 'address' ? 'bg-purple-600' : 'bg-white/10 text-white border-white/20'}`}
                  >
                    <Search className="w-4 h-4 mr-1" /> Adresse
                  </Button>
                  <Button
                    variant={searchMode === 'cadastre' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSearchMode('cadastre')}
                    className={`flex-1 min-w-[80px] ${searchMode === 'cadastre' ? 'bg-purple-600' : 'bg-white/10 text-white border-white/20'}`}
                  >
                    <FileText className="w-4 h-4 mr-1" /> Cadastre
                  </Button>
                </div>

                {/* GPS Search */}
                {searchMode === 'gps' && (
                  <Button
                    onClick={handleGetLocation}
                    disabled={loading}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Navigation className="w-4 h-4 mr-2" />
                    )}
                    Utiliser ma position
                  </Button>
                )}

                {/* Address Search */}
                {searchMode === 'address' && (
                  <div className="space-y-2">
                    <div className="relative">
                      <Input
                        value={addressSearch}
                        onChange={(e) => {
                          const val = e.target.value;
                          setAddressSearch(val);
                          setAddressValidatedRadar(false);
                          if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
                          if (val.length >= 3) {
                            addressDebounceRef.current = setTimeout(async () => {
                              try {
                                const res = await addressApi.autocomplete(val);
                                setAddressSuggestions(res.data || []);
                                setShowAddressSuggestions(true);
                              } catch {}
                            }, 400);
                          } else {
                            setShowAddressSuggestions(false);
                          }
                        }}
                        onBlur={() => setTimeout(() => setShowAddressSuggestions(false), 200)}
                        placeholder="12 rue des Lilas, Aubagne"
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/50 pr-10"
                        onKeyPress={(e) => e.key === 'Enter' && handleAddressSearch()}
                      />
                      <button
                        type="button"
                        onClick={handleMicRadar}
                        title="Dicter l'adresse"
                        data-testid="radar-mic-btn"
                        className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full transition-all ${
                          isListeningRadar
                            ? 'bg-red-500/30 text-red-300 animate-pulse'
                            : 'text-white/50 hover:text-white hover:bg-white/10'
                        }`}
                      >
                        {isListeningRadar ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                      </button>
                      {showAddressSuggestions && addressSuggestions.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                          {addressSuggestions.map((s, i) => (
                            <button
                              key={i}
                              type="button"
                              className="w-full text-left px-4 py-2.5 hover:bg-purple-50 text-sm text-gray-800 transition-colors border-b last:border-0"
                              onMouseDown={() => {
                                const desc = s.description || s.label || '';
                                setAddressSearch(desc);
                                setAddressValidatedRadar(true);
                                setShowAddressSuggestions(false);
                              }}
                            >
                              {s.description || s.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button
                      onClick={handleAddressSearch}
                      disabled={loading || (addressSearch.length >= 3 && !addressValidatedRadar)}
                      className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                      Rechercher
                    </Button>
                    {addressSearch.length >= 3 && !addressValidatedRadar && (
                      <p className="text-xs text-yellow-300 flex items-center gap-1 mt-1">
                        <span>⚠</span> Sélectionnez une adresse dans la liste pour lancer la recherche
                      </p>
                    )}
                  </div>
                )}

                {/* Cadastre Search */}
                {searchMode === 'cadastre' && (
                  <div className="space-y-2">
                    <Input
                      value={cadastreSearch.commune}
                      onChange={(e) => setCadastreSearch({...cadastreSearch, commune: e.target.value})}
                      placeholder="Commune (ex: Aubagne)"
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        value={cadastreSearch.section}
                        onChange={(e) => setCadastreSearch({...cadastreSearch, section: e.target.value})}
                        placeholder="Section (ex: AB)"
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                      />
                      <Input
                        value={cadastreSearch.numero}
                        onChange={(e) => setCadastreSearch({...cadastreSearch, numero: e.target.value})}
                        placeholder="N° (ex: 12)"
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                      />
                    </div>
                    <Button
                      onClick={handleCadastreSearch}
                      disabled={loading}
                      className="w-full bg-purple-600 hover:bg-purple-700"
                    >
                      {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                      Rechercher
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Selected Parcelle Info */}
            {selectedParcelle && (
              <Card className="bg-white/10 border-white/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-green-400" />
                    Parcelle sélectionnée
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-white">
                  <div className="space-y-2 mb-4">
                    <p><span className="text-white/60">Commune :</span> {selectedParcelle.commune} ({selectedParcelle.code_postal})</p>
                    <p><span className="text-white/60">Section :</span> {selectedParcelle.section}</p>
                    <p><span className="text-white/60">Numéro :</span> {selectedParcelle.numero}</p>
                    <p><span className="text-white/60">Surface :</span> {selectedParcelle.surface ? `${selectedParcelle.surface} m²` : 'Non renseignée'}</p>
                  </div>

                  {/* Actions */}
                  <div className="space-y-3">
                    <Button
                      onClick={() => clientId ? handleSendPlan() : setShowRegisterModal(true)}
                      variant="outline"
                      className="w-full bg-white/10 border-white/30 text-white hover:bg-white/20"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Recevoir le plan (Gratuit)
                    </Button>
                    
                    {!partnerSubdomain && (
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg blur opacity-50"></div>
                      <Button
                        onClick={() => clientId ? setShowPaymentModal(true) : setShowRegisterModal(true)}
                        className="relative w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                      >
                        <User className="w-4 h-4 mr-2" />
                        Qui est le propriétaire ? - 49€
                      </Button>
                    </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {parcelleLoading && (
              <Card className="bg-white/10 border-white/20">
                <CardContent className="py-8 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-400 mx-auto mb-2" />
                  <p className="text-white/60">Recherche de la parcelle...</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Map (appears second on mobile, right on desktop) */}
          <div className="lg:col-span-2 order-2 lg:order-2">
            <Card className="bg-white/10 border-white/20 overflow-hidden h-[400px] lg:h-[600px]">
              <MapContainer
                center={mapCenter}
                zoom={13}
                style={{ height: '100%', width: '100%' }}
                className="rounded-lg"
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <TileLayer
                  url="https://inspire.cadastre.gouv.fr/scpc/wms?service=wms&version=1.3&request=GetMap&layers=parcelles&styles=&crs=EPSG:3857&format=image/png&transparent=true"
                  opacity={0.5}
                />
                <LocationMarker position={position} setPosition={setPosition} />
              </MapContainer>
            </Card>
            <p className="text-white/50 text-sm mt-2 text-center">
              Cliquez sur la carte pour sélectionner une parcelle
            </p>
          </div>
        </div>
      </main>

      {/* Registration Modal */}
      <Dialog open={showRegisterModal} onOpenChange={setShowRegisterModal}>
        <DialogContent className="bg-slate-900 border-white/20 text-white z-[9999]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-purple-400" />
              Inscription rapide
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-white/70">
              Inscrivez-vous pour accéder aux informations de cette parcelle.
            </p>
            <div>
              <Label className="text-white/80">Email *</Label>
              <Input
                type="email"
                value={registerForm.email}
                onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                placeholder="votre@email.com"
                className="bg-white/10 border-white/20 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-white/80">Téléphone <span className="text-white/40 font-normal">(facultatif)</span></Label>
              <Input
                type="tel"
                value={registerForm.telephone}
                onChange={(e) => setRegisterForm({...registerForm, telephone: e.target.value})}
                placeholder="06 12 34 56 78"
                className="bg-white/10 border-white/20 text-white mt-1"
              />
            </div>
            <RgpdCheckbox checked={rgpdRadar} onChange={setRgpdRadar} companyName={partner?.name || null} />
            <Button
              onClick={handleRegister}
              disabled={registering || !registerForm.email || !rgpdRadar}
              className="w-full bg-purple-600 hover:bg-purple-700 mt-2"
            >
              {registering ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              S'inscrire
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Confirmation Modal */}
      {!partnerSubdomain && (
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="bg-slate-900 border-white/20 text-white max-w-md z-[9999]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-purple-400" />
              Qui est le propriétaire ?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedParcelle && (
              <div className="bg-white/5 p-4 rounded-lg">
                <p className="text-white/60 text-sm">Parcelle sélectionnée :</p>
                <p className="font-medium">{selectedParcelle.section}-{selectedParcelle.numero}, {selectedParcelle.commune}</p>
              </div>
            )}
            
            <div className="bg-purple-500/20 p-4 rounded-lg border border-purple-500/30">
              <h4 className="font-medium mb-2">Ce que vous allez recevoir :</h4>
              <ul className="text-sm text-white/80 space-y-1">
                <li>✅ Nom et prénom du propriétaire actuel</li>
                <li>✅ Adresse du propriétaire</li>
              </ul>
            </div>
            
            <div className="bg-blue-500/20 p-4 rounded-lg border border-blue-500/30">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-blue-400" />
                <span className="font-medium">Délai : 7 à 10 jours ouvrés</span>
              </div>
              <p className="text-sm text-white/70">
                Notre expert effectue les recherches nécessaires.
                Vous serez notifié par email dès confirmation.
              </p>
            </div>
            
            <div className="text-center py-2">
              <span className="text-3xl font-bold">49 €</span>
              <span className="text-white/60 ml-2">TTC</span>
            </div>
            
            <Button
              onClick={handlePayment}
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 py-6 text-lg"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <CreditCard className="w-5 h-5 mr-2" />
              )}
              Payer 49 € et obtenir l'identité
            </Button>
            
            <p className="text-center text-white/50 text-xs">
              🔒 Paiement sécurisé par Stripe
            </p>
          </div>
        </DialogContent>
      </Dialog>
      )}

      {/* Modal Suivre ma demande */}
      <Dialog open={showSuiviModal} onOpenChange={setShowSuiviModal}>
        <DialogContent className="bg-gray-900 border-white/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-center text-xl">Suivre ma demande</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-white/60 text-sm text-center">
              Retrouvez votre demande avec votre email et votre code de suivi reçu par mail
            </p>
            <div>
              <Label className="text-white/80">Email</Label>
              <Input
                type="email"
                value={suiviForm.email}
                onChange={(e) => setSuiviForm({...suiviForm, email: e.target.value})}
                placeholder="votre@email.com"
                className="bg-white/10 border-white/20 text-white mt-1"
                data-testid="suivi-email-input"
              />
            </div>
            <div>
              <Label className="text-white/80">Code de suivi</Label>
              <Input
                value={suiviForm.code}
                onChange={(e) => setSuiviForm({...suiviForm, code: e.target.value.toUpperCase()})}
                placeholder="RAD-2026-00001"
                className="bg-white/10 border-white/20 text-white mt-1 tracking-widest font-bold text-center text-lg"
                data-testid="suivi-code-input"
              />
            </div>
            <Button
              onClick={handleSuiviByCode}
              disabled={suiviLoading || !suiviForm.email || !suiviForm.code}
              className="w-full bg-purple-600 hover:bg-purple-700"
              data-testid="suivi-submit-btn"
            >
              {suiviLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Accéder à ma demande
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
